import { useTranslations } from '@aikoa/i18n/react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'expo-router'
import { useState } from 'react'
import { Pressable, View } from 'react-native'
import { useDebouncedCallback } from 'use-debounce'
import ContentBox from '~/components/layout/ContentBox'
import ScreenView from '~/components/layout/ScreenView'
import { Body, Label } from '~/components/typography/Content'
import { Heading2 } from '~/components/typography/Titles'
import { Button, ButtonLabel } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Text } from '~/components/ui/text'
import { trpc } from '~/lib/trpc'

type InvitationListRow = {
  id: string
  email: string
  phone: string | null
  inviterName: string | null
  inviterUserId: string | null
  inviterUserName: string | null
  userId: string | null
  userName: string | null
  createdAt: string
  updatedAt: string
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleDateString('fi-FI', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function InvitationRow({ invitation }: { invitation: InvitationListRow }) {
  const { t } = useTranslations('admin')
  const isProcessed = !!invitation.userId

  const href = isProcessed
    ? `/(authenticated)/admin/users/${invitation.userId}`
    : `/(authenticated)/admin/users/add?inviteId=${invitation.id}`

  return (
    <Link href={href} asChild>
      <Pressable className="gap-3 rounded-lg border border-neutral-200 bg-white p-4 active:bg-neutral-50">
        <View className="flex-row items-start justify-between gap-4">
          <View className="flex-1 gap-1">
            <Text className="font-medium text-base">{invitation.email}</Text>

            {!!invitation.phone && <Label className="text-sm text-secondary">{invitation.phone}</Label>}

            {!!invitation.inviterName && (
              <Label className="text-sm text-secondary">
                {t(($) => $.invitations.inviterNamed, {
                  name: invitation.inviterName,
                })}
              </Label>
            )}
          </View>

          <View
            className={`rounded-md px-3 py-1.5 ${
              isProcessed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
            }`}>
            <Text className="font-medium text-sm">
              {isProcessed
                ? t(($) => $.invitations.statuses.processed)
                : t(($) => $.invitations.statuses.unprocessed)}
            </Text>
          </View>
        </View>

        <View className="flex-row flex-wrap gap-4">
          <View className="gap-0.5">
            <Label className="text-xs text-neutral-400">{t(($) => $.invitations.fields.created)}</Label>
            <Label className="text-sm text-secondary">{formatDateTime(invitation.createdAt)}</Label>
          </View>

          {isProcessed && (
            <View className="gap-0.5">
              <Label className="text-xs text-neutral-400">{t(($) => $.invitations.fields.user)}</Label>
              <Label className="text-sm text-secondary">
                {invitation.userName ?? t(($) => $.invitations.unknownUser)}
              </Label>
            </View>
          )}
        </View>
      </Pressable>
    </Link>
  )
}

const PAGE_SIZE = 20

export default function Invitations() {
  const { t } = useTranslations('admin')
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')

  const debouncedSetSearch = useDebouncedCallback((value: string) => {
    setSearch(value)
    setPage(1)
  }, 300)

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    debouncedSetSearch(value)
  }

  const queryInput = { page, limit: PAGE_SIZE, search: search || undefined }

  const { data, isLoading, error, isFetching } = useQuery(
    trpc.admin.listUserInvitations.queryOptions(queryInput)
  )

  const invitations = (data?.invitations ?? []) as InvitationListRow[]
  const totalPages = data?.totalPages ?? 1
  const totalCount = data?.totalCount ?? 0
  const currentPage = data?.page ?? page

  const canGoBack = currentPage > 1
  const canGoNext = currentPage < totalPages

  return (
    <ScreenView>
      <ContentBox className="flex-1" contentContainerClassName="gap-6" variant="transparent">
        <View className="gap-4">
          <Heading2 className="text-center">{t(($) => $.invitations.title)}</Heading2>
          <Body className="text-center text-neutral-600">{t(($) => $.invitations.description)}</Body>
        </View>

        <Input
          value={searchInput}
          onChangeText={handleSearchChange}
          placeholder={t(($) => $.invitations.searchPlaceholder)}
          variant="box"
          className="bg-white"
        />

        {isLoading && (
          <Body className="text-center text-neutral-500">{t(($) => $.invitations.loading)}</Body>
        )}

        {!!error && <Body className="text-center text-red-600">{t(($) => $.invitations.loadError)}</Body>}

        {!isLoading && !error && invitations.length === 0 && (
          <Body className="text-center text-neutral-500">
            {search ? t(($) => $.invitations.noResults) : t(($) => $.invitations.none)}
          </Body>
        )}

        {invitations.length > 0 && (
          <>
            <View className="gap-3">
              {invitations.map((invitation) => (
                <InvitationRow key={invitation.id} invitation={invitation} />
              ))}
            </View>

            <View className="flex-row items-center justify-between gap-4 pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!canGoBack || isFetching}
                onPress={() => setPage(currentPage - 1)}>
                <ButtonLabel>{t(($) => $.invitations.previous)}</ButtonLabel>
              </Button>

              <Text className="text-sm text-neutral-600">
                {t(($) => $.invitations.page, {
                  current: currentPage,
                  total: totalPages,
                  count: totalCount,
                })}
              </Text>

              <Button
                variant="outline"
                size="sm"
                disabled={!canGoNext || isFetching}
                onPress={() => setPage(currentPage + 1)}>
                <ButtonLabel>{t(($) => $.invitations.next)}</ButtonLabel>
              </Button>
            </View>
          </>
        )}
      </ContentBox>
    </ScreenView>
  )
}
